import React, { useCallback, useEffect, useState } from 'react';

import { DialogModal, MenuItem, ModalBox, Table } from 'components';
import { useIsMobile } from 'hooks';
import { Edit2, Personalcard, Trash } from 'iconsax-react';

import { PayeeCreateEditForm } from './PayeeCreateEditForm';
import { PayeeExportModal, PayeeExportOptions } from './PayeeExportModal';
import { payeeColumns } from './payeeColumns';

import { dayjs, useSelector, useTranslation } from 'third-party';

import {
  DEFAULT_TIMEZONE,
  PersisTableFilters,
  PersisTableSort,
  PersisTables,
} from 'constants/common';

import { usePayee } from 'modules/payee';

import { appSelectors, payeeSelectors, settingSelectors } from 'selectors';

import { FilterValue } from 'components/Table/types';

import {
  downloadExportFile,
  extractFiltersFromSavedColumns,
  hasRights,
  mergeSavedColumnsWithExisting,
} from 'utils/app';
import {
  getFromStorage,
  removeFromStorage,
  saveInStorage,
} from 'utils/storage';

import {
  APIPayee,
  APISettingName,
  APISortOrder,
  APIUserRight,
} from 'types/api';

import { palette } from 'styles/theme';

const DEFAULT_PAYEE_SORT = {
  sortField: 'createdOn',
  sortOrder: APISortOrder.DESC,
};

export const PayeeList = () => {
  const { t } = useTranslation();
  const timezoneValue = useSelector(
    settingSelectors.getSettingValue(APISettingName.Timezone, DEFAULT_TIMEZONE),
  );
  const [columns, setColumns] = useState(() => payeeColumns(timezoneValue));
  const [selectedItem, setSelectedItem] = useState<APIPayee | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const isMobile = useIsMobile();

  const {
    loadPayees,
    loadMorePayees,
    updatePayeeFilters,
    deletePayee,
    clearPayeeList,
    resetPayeeFilters,
    exportPayees,
  } = usePayee();

  const [loadedUserSettings, setLoadedUserSettings] = useState(false);
  const [isFilteringOn, setIsFilteringOn] = useState(false);
  const items = useSelector(payeeSelectors.payeeList);
  const organizationId = useSelector(appSelectors.organizationId);
  const payeesListLoaded = useSelector(payeeSelectors.payeesListLoaded);
  const allPayeesLoaded = useSelector(payeeSelectors.allPayeesLoaded);
  const payeeFilters = useSelector(payeeSelectors.payeeFilters);
  const organizations = useSelector(appSelectors.organizations);
  const loadPayeesPending = useSelector(payeeSelectors.loadPayeesPending);
  const loadMorePayeesPending = useSelector(
    payeeSelectors.loadMorePayeesPending,
  );
  const exportPayeesPending = useSelector(payeeSelectors.exportPayeesPending);
  const hasAdminAccess = hasRights(
    organizations,
    APIUserRight.PayeeDirectory,
    organizationId,
  );

  useEffect(() => {
    return () => {
      clearPayeeList();
      resetPayeeFilters();
    };
  }, [clearPayeeList, resetPayeeFilters]);

  useEffect(() => {
    if (loadedUserSettings) {
      loadPayees();
    }
  }, [payeeFilters, loadedUserSettings, loadPayees]);

  useEffect(() => {
    const savedColumns = getFromStorage(
      PersisTables.Payees,
      payeeColumns(timezoneValue),
    );
    const savedSort = getFromStorage(
      PersisTableSort.Payees,
      DEFAULT_PAYEE_SORT,
    );

    const columnFilters = savedColumns
      ? extractFiltersFromSavedColumns(savedColumns)
      : {};

    updatePayeeFilters({ ...columnFilters, searchSortOrders: [savedSort] });

    if (savedColumns) {
      setColumns(mergeSavedColumnsWithExisting(savedColumns, columns));
    }
    setIsFilteringOn(getFromStorage(PersisTableFilters.Payees, false));
    setLoadedUserSettings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const onColumnFilterChange = useCallback(
    (columnId: string, value: string) => {
      setColumns(
        columns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filter: value };
          }
          return column;
        }),
      );

      updatePayeeFilters({ ...payeeFilters, [columnId]: value });
    },
    [payeeFilters, columns, updatePayeeFilters],
  );

  const onAdvancedFilterChange = useCallback(
    (columnId: string, value: FilterValue) => {
      setColumns(
        columns.map(column => {
          if (column.dataId === columnId) {
            return { ...column, filterValue: value };
          }
          return column;
        }),
      );

      updatePayeeFilters({ ...payeeFilters, [columnId]: value });
    },
    [payeeFilters, columns, updatePayeeFilters],
  );

  const onFilterReset = useCallback(() => {
    resetPayeeFilters();
    setColumns(it =>
      it.map(column => ({
        ...column,
        filter: undefined,
        filterValue: undefined,
      })),
    );
  }, [resetPayeeFilters]);

  const onColumnVisibilityChanged = useCallback(
    (columnId: string, value: boolean) => {
      setColumns(it =>
        it.map(column => {
          if (column.dataId === columnId) {
            return { ...column, hidden: !value };
          }
          return column;
        }),
      );
    },
    [],
  );

  const rowOptionsMenu = useCallback(
    (item: APIPayee, onRowMenuClose: () => void): React.ReactElement[] => [
      <MenuItem
        key={'delete'}
        onClick={() => {
          setSelectedItem(item);
          onRowMenuClose();
        }}
      >
        <Trash
          variant="TwoTone"
          color={palette.primary900}
          style={{ paddingRight: '0.5rem' }}
        />
        {t('table.delete')}
      </MenuItem>,
      <MenuItem
        key={'edit'}
        onClick={() => {
          setSelectedItem(item);
          setIsModalOpen(true);
          onRowMenuClose();
        }}
      >
        <Edit2
          variant="TwoTone"
          color={palette.primary900}
          style={{ paddingRight: '0.5rem' }}
        />
        {t('table.edit')}
      </MenuItem>,
    ],
    [t],
  );

  const onModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedItem(null);
  }, []);

  const onColumnHeaderClicked = useCallback(
    (columnDataId: string) => {
      const existingSortOrder = payeeFilters.searchSortOrders?.find(
        order => order.sortField === columnDataId,
      );

      const newSortOrder =
        existingSortOrder && existingSortOrder.sortOrder === APISortOrder.DESC
          ? APISortOrder.ASC
          : APISortOrder.DESC;

      const newSort = { sortField: columnDataId, sortOrder: newSortOrder };

      saveInStorage(PersisTableSort.Payees, newSort);

      updatePayeeFilters({
        ...payeeFilters,
        searchSortOrders: [newSort],
      });
    },
    [payeeFilters, updatePayeeFilters],
  );

  const onExportClick = () => {
    setIsExportModalOpen(true);
  };

  const onExportConfirm = async (options: PayeeExportOptions) => {
    const response = await exportPayees(options);
    const ext = options.format === 'Csv' ? 'csv' : 'xlsx';
    downloadExportFile(
      response.payload,
      `payees-export-${dayjs().format('YYYY-MM-DD')}.${ext}`,
    );
    setIsExportModalOpen(false);
  };

  return (
    <>
      <Table<APIPayee>
        draggable
        header={t('payeeList.header')}
        subHeader={t('payeeList.subHeader')}
        headerIcon={
          <Personalcard
            variant="TwoTone"
            color={palette.primary900}
            size="28"
          />
        }
        onSaveTableSettings={settings => {
          saveInStorage(PersisTables.Payees, settings);
        }}
        onResetTableSettings={() => {
          setColumns(payeeColumns(timezoneValue));
          updatePayeeFilters({ searchSortOrders: [DEFAULT_PAYEE_SORT] });
          removeFromStorage(PersisTables.Payees);
          removeFromStorage(PersisTableSort.Payees);
        }}
        onColumnSizeChanged={(it, width) => {
          setColumns(prevColumns =>
            prevColumns.map(column =>
              column.dataId === it ? { ...column, width: width } : column,
            ),
          );
        }}
        onColumnOrderChanged={setColumns}
        filterable
        filteringOn={isFilteringOn}
        onFilteringToggle={value => {
          setIsFilteringOn(value);
          saveInStorage(PersisTableFilters.Payees, value);
        }}
        allItemsLoaded={allPayeesLoaded}
        infiniteScroll
        tableMinHeight="20rem"
        columns={columns}
        items={items || []}
        sortField={
          payeeFilters.searchSortOrders?.[0]?.sortField as keyof APIPayee
        }
        sortOrder={payeeFilters.searchSortOrders?.[0]?.sortOrder}
        rowOptionsMenu={hasAdminAccess ? rowOptionsMenu : undefined}
        onColumnHeaderClicked={onColumnHeaderClicked}
        onColumnFilterChange={onColumnFilterChange}
        onAdvancedFilterChange={onAdvancedFilterChange}
        onColumnVisibilityChanged={onColumnVisibilityChanged}
        isExportPending={exportPayeesPending}
        onExportClick={onExportClick}
        loadItemsPending={loadPayeesPending}
        loadMoreItemsPending={loadMorePayeesPending}
        onLoadMoreClicked={loadMorePayees}
        onFilterReset={onFilterReset}
        bodyRowHeight="1rem"
        headerRowHeight="0.2rem"
        itemsLoaded={payeesListLoaded}
        onNewItemButtonClicked={
          hasAdminAccess ? () => setIsModalOpen(true) : undefined
        }
        emptyText={t('table.empty')}
        loadMoreButtonText={t('table.loadMore')}
      />
      {isModalOpen && (
        <ModalBox
          width={isMobile ? '90%' : '30rem'}
          height="39rem"
          onClose={onModalClose}
        >
          <PayeeCreateEditForm
            organizationId={organizationId}
            onModalClose={onModalClose}
            payeeId={selectedItem?.id}
          />
        </ModalBox>
      )}
      <DialogModal
        onClose={() => setSelectedItem(null)}
        open={!!selectedItem && !isModalOpen}
        description={t('payeeList.deleteConfirm')}
        handleActionClick={() => {
          if (selectedItem) {
            deletePayee(selectedItem.id);
          }
          setSelectedItem(null);
        }}
      />
      {isExportModalOpen && (
        <PayeeExportModal
          onClose={() => setIsExportModalOpen(false)}
          onExport={onExportConfirm}
          isPending={exportPayeesPending}
        />
      )}
    </>
  );
};
